import robustFetch from "@/utils/robustFetch";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

interface AdminAuthGuardProps {
  children: React.ReactNode;
}

interface VerificationStatus {
  authenticated: boolean;
  emailVerified: boolean;
  email?: string;
  requiresVerification: boolean;
}

export default function AdminAuthGuard({ children }: AdminAuthGuardProps) {
  const [verificationStatus, setVerificationStatus] =
    useState<VerificationStatus | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    checkAdminAuthentication();
  }, [location.pathname]);

  const checkAdminAuthentication = async () => {
    const token = localStorage.getItem("auth_token");

    if (!token) {
      setVerificationStatus({
        authenticated: false,
        emailVerified: false,
        requiresVerification: false,
      });
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    try {
      // Check user authentication and verification status
      const response = await robustFetch("/api/verification-status", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const status = await response.json();
        setVerificationStatus(status);

        // Check if user is admin by comparing email with admin email from environment
        const adminEmail = "admin@docpose.cloud"; // This could also be fetched from API
        setIsAdmin(status.authenticated && status.email === adminEmail);
      } else {
        // Invalid token
        if (response.status === 401) {
          localStorage.removeItem("auth_token");
          localStorage.removeItem("user_data");
          // SECURITY FIX: Clear session on auth failure
          localStorage.removeItem("session_id");
          const newGuestSession = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          localStorage.setItem("session_id", newGuestSession);
        }
        setVerificationStatus({
          authenticated: false,
          emailVerified: false,
          requiresVerification: false,
        });
        setIsAdmin(false);
      }
    } catch (error) {
      console.error("Error checking admin authentication:", error);
      setVerificationStatus({
        authenticated: false,
        emailVerified: false,
        requiresVerification: false,
      });
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loading) return;

    if (!verificationStatus?.authenticated) {
      // Store the current path to redirect back after login
      localStorage.setItem(
        "redirect_after_login",
        location.pathname + location.search,
      );
      navigate("/login", { replace: true });
      return;
    }

    if (verificationStatus.authenticated && !isAdmin) {
      // User is authenticated but not admin - redirect to dashboard
      navigate("/dashboard", { replace: true });
      return;
    }

    if (
      verificationStatus.authenticated &&
      verificationStatus.requiresVerification
    ) {
      // Admin user needs email verification
      navigate("/verify-email", { replace: true });
      return;
    }
  }, [verificationStatus, isAdmin, loading, navigate, location]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-blue-600 rounded-full animate-pulse"></div>
          <div
            className="w-4 h-4 bg-blue-600 rounded-full animate-pulse"
            style={{ animationDelay: "0.2s" }}
          ></div>
          <div
            className="w-4 h-4 bg-blue-600 rounded-full animate-pulse"
            style={{ animationDelay: "0.4s" }}
          ></div>
        </div>
      </div>
    );
  }

  // If not authenticated or not admin, return null (navigation will happen in useEffect)
  if (!verificationStatus?.authenticated || !isAdmin) {
    return null;
  }

  // User is authenticated admin with verified email, render children
  return <>{children}</>;
}
